import { useState } from 'react';
import { SparklesIcon, MapIcon } from '@heroicons/react/24/outline';
import type { SupportHelp, SupportStep } from '../hooks/useSupportHelp';
import { GuidedTour } from './GuidedTour';
import { Markdown } from './Markdown';

/**
 * Sección "Asistente IA" para los modales de error: muestra la explicación, los
 * pasos sugeridos y un botón "Guíame en pantalla" que ejecuta esos pasos en el
 * portal real del cliente (navega + resalta). Se rinde nada si no aplica.
 *
 * Si recibe `onGuide`, delega el arranque del tour al contenedor (p. ej. para que
 * cierre su modal primero); si no, lo ejecuta internamente.
 */
export function SupportAssistantPanel({
  help,
  onGuide,
  guideEnabled = true,
}: {
  help: SupportHelp;
  onGuide?: (steps: SupportStep[]) => void;
  guideEnabled?: boolean;
}) {
  const [touring, setTouring] = useState(false);
  if (!help.loading && !help.reply && !help.failed) return null;

  const hasSteps = !!help.steps && help.steps.length > 0;
  // El botón "Guíame en pantalla" (guía interactiva) solo si la feature está activa.
  const canGuide = hasSteps && guideEnabled;

  function startGuide() {
    if (onGuide) onGuide(help.steps);
    else setTouring(true);
  }

  return (
    <div className="mt-4 rounded-xl border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <SparklesIcon className="h-5 w-5 text-primary-600" />
        <h4 className="text-sm font-semibold text-secondary-900 dark:text-white">Asistente IA</h4>
      </div>

      {help.loading && (
        <div className="flex items-center gap-2 text-sm text-secondary-500">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          Analizando el error…
        </div>
      )}

      {help.failed && (
        <p className="text-sm text-secondary-500">
          No se pudo obtener la ayuda del asistente en este momento.
        </p>
      )}

      {/* 1) POR QUÉ pasó + qué hacer (explicación del agente). */}
      {help.reply && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-1">
            Por qué pasó
          </p>
          <Markdown text={help.reply} className="text-sm text-secondary-700 dark:text-secondary-300" />
        </div>
      )}

      {/* 2) PASOS TEXTUALES (numerados) para resolverlo a mano. */}
      {hasSteps && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-1">
            Pasos para resolverlo
          </p>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-secondary-700 dark:text-secondary-300">
            {help.steps
              .filter((s) => s.label)
              .map((s, i) => (
                <li key={i}>
                  <Markdown text={s.label as string} className="inline" />
                </li>
              ))}
          </ol>
        </div>
      )}

      {/* 3) Opción APARTE: que el asistente te lleve de la mano en pantalla. */}
      {canGuide && (
        <div className="mt-4 flex flex-col gap-2 border-t border-secondary-200 dark:border-secondary-700 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-secondary-600 dark:text-secondary-400">
            ¿Prefieres que te lleve de la mano?
          </span>
          <button
            type="button"
            onClick={startGuide}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <MapIcon className="h-4 w-4" /> Guíame en pantalla
          </button>
        </div>
      )}

      {/* El feedback "¿te funcionó?" ahora es un toast en la esquina (persiste tras
          cerrar el modal) — lo dispara SupportErrorProvider. */}

      {/* Tour interno (solo si NO hay onGuide; si lo hay, lo maneja el contenedor). */}
      {!onGuide && touring && hasSteps && (
        <GuidedTour steps={help.steps} onClose={() => setTouring(false)} />
      )}
    </div>
  );
}
